import React, {useCallback, useEffect, useRef, useState} from "react";
import ReactDOM from "react-dom";
import Autosuggest, {
  type ChangeEvent,
  type InputProps,
  type RenderInputComponentProps,
} from "react-autosuggest";
import Trie from "./Trie";
import {getTickerType, TickerType} from "../utils/typeValidation";

// Define suggestion type
type Suggestion = string;

// Get suggestions from the trie
const getSuggestions = (trie: Trie, value: string) => {
  const suggestions = trie.suggestions(value.trim().toUpperCase());
  return suggestions.sort();
};

// Get suggestion value
const getSuggestionValue = (suggestion: string) => suggestion;

// Render suggestion with the typed prefix highlighted
const renderSuggestion = (suggestion: string, {query}: {query: string}) => {
  const upperQuery = query.trim().toUpperCase();
  const matchLen = upperQuery.length;
  const prefix = suggestion.slice(0, matchLen);
  const rest = suggestion.slice(matchLen);

  return (
    <div className="suggestion-item">
      <span className="suggestion-match">{prefix}</span>
      {rest && <span>{rest}</span>}
    </div>
  );
};

interface Props {
  data: string[];
  onChangeProp: (newValue: string) => void;
  onSelectProp?: (selectedValue: string) => void;
  value?: string;
  typeFilter?: TickerType | 'all';
}

export default function Autocomplete(props: Props) {
  const {data, onChangeProp, onSelectProp, value: propValue, typeFilter} = props;
  const [value, setValue] = useState(propValue || "");
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [trie, setTrie] = useState(new Trie(data));
  const [isFocused, setIsFocused] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Update input value when prop value changes
  useEffect(() => {
    if (propValue !== undefined) {
      setValue(propValue);
    }
  }, [propValue]);

  // Update trie when data or typeFilter changes
  useEffect(() => {
    const filtered = (!typeFilter || typeFilter === 'all')
      ? data
      : data.filter(t => getTickerType(t) === typeFilter);
    setTrie(new Trie(filtered));
  }, [data, typeFilter]);

  // Handle input changes
  const onChange = (event: React.FormEvent<HTMLElement>, params: ChangeEvent) => {
    const updatedValue =
      params.newValue.toUpperCase() ||
      (event.target as HTMLInputElement).value.toUpperCase();
    onChangeProp(updatedValue);
    setValue(updatedValue);
  };

  // Fetch suggestions
  const onSuggestionsFetchRequested = useCallback(
    ({value}: {value: string}) => {
      setSuggestions(getSuggestions(trie, value));
    },
    [trie],
  );

  // Clear suggestions
  const onSuggestionsClearRequested = () => {
    setSuggestions([]);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const TypedAutosuggest = Autosuggest as unknown as React.ComponentType<any>;

  // Handle focus events
  const onFocus = () => setIsFocused(true);
  const onBlur = () => setIsFocused(false);

  // Custom render input component
  const renderInputComponent = (inputProps: RenderInputComponentProps) => {
    const {key, ...restProps} = inputProps as RenderInputComponentProps & {
      key?: string;
    };
    return (
      <div className={`ticker-input-container ${isFocused ? "focused" : ""}`}>
        <input
          key={key}
          {...(restProps as React.InputHTMLAttributes<HTMLInputElement>)}
        />
        {value && (
          <button
            type="button"
            className="clear-button"
            onClick={() => {
              setValue("");
              onChangeProp("");
            }}
          >
            ×
          </button>
        )}
        <div className="ticker-icon"></div>
      </div>
    );
  };

  // Render the suggestions dropdown in a portal so it escapes any ancestor
  // overflow:hidden (e.g. Bootstrap's Card with border-radius).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderSuggestionsContainer = useCallback(({containerProps, children}: any) => {
    // Compute position from the wrapper div every time the dropdown opens
    let posStyle: React.CSSProperties = {position: "fixed", zIndex: 9999};
    if (wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      posStyle = {
        position: "fixed",
        top: rect.bottom + 5,
        left: rect.left,
        width: rect.width,
        zIndex: 9999,
      };
    }
    const {key, ...restContainerProps} = containerProps;
    return ReactDOM.createPortal(
      <div key={key} {...restContainerProps} style={posStyle}>
        {children}
      </div>,
      document.body,
    );
  }, []);

  // Theme for styling Autosuggest components
  const theme = {
    container: "autosuggest-container",
    suggestionsContainer: "suggestions-container",
    suggestionsContainerOpen: "suggestions-container-open",
    suggestionsList: "suggestions-list",
    suggestion: "suggestion",
    suggestionHighlighted: "suggestion-highlighted",
    input: "ticker-input form-control",
  };

  // Input props
  const inputProps: InputProps<string> = {
    placeholder: "Enter ticker symbol...",
    value,
    onChange,
    onFocus,
    onBlur,
  };

  return (
    <div className="autocomplete-wrapper" ref={wrapperRef}>
      <TypedAutosuggest
        suggestions={suggestions}
        onSuggestionsFetchRequested={onSuggestionsFetchRequested}
        onSuggestionsClearRequested={onSuggestionsClearRequested}
        onSuggestionSelected={(_event: React.FormEvent, {suggestion}: {suggestion: string}) => {
          onSelectProp?.(suggestion);
        }}
        getSuggestionValue={getSuggestionValue}
        renderSuggestion={renderSuggestion}
        renderInputComponent={renderInputComponent}
        renderSuggestionsContainer={renderSuggestionsContainer}
        inputProps={inputProps}
        theme={theme}
        highlightFirstSuggestion
      />
    </div>
  );
}
